import React, {useState} from 'react';
import {Platform, View, StyleSheet, TouchableOpacity} from 'react-native';
import {Tab} from 'react-native-elements';
import QRCode from 'react-native-qrcode-svg';
import {width, height, totalSize} from 'react-native-dimension';

import {colorPalette, colors} from '../../../core/config/color.config';
import {RNEText, RNEButton, SVGIcon} from '../../components';

interface ShareDoctorProfilePanelProps {
  shareUrl: string;
  onClose?: () => void;
  onCopyLink: () => void;
  onShareLink: () => void;
  onDownloadQr: () => void;
  onShareQr: () => void;
  getQrRef: (ref: any) => void;
}

const QR_SIZE = Platform.OS === 'web' ? 220 : totalSize(28);

/**
 * Pure presentational panel shared by the web and native Share modal
 * containers (ShareDoctorProfileModal.tsx / .native.tsx). Deliberately has
 * no dependency on any package that only works on one platform (Clipboard,
 * react-native-share, camera-roll, etc. all internally use TurboModuleRegistry,
 * which react-native-web doesn't implement - importing them anywhere reachable
 * from the web bundle breaks the web build even behind a Platform.OS check,
 * since bundlers resolve the whole import graph statically). Those live only
 * in the platform-specific containers, passed in here as plain callbacks.
 */
const ShareDoctorProfilePanel = ({
  shareUrl,
  onClose,
  onCopyLink,
  onShareLink,
  onDownloadQr,
  onShareQr,
  getQrRef,
}: ShareDoctorProfilePanelProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <RNEText
          label="Share Doctor Profile"
          variant="subheader"
          weight="600"
          color="accent"
        />
        {!!onClose && (
          <TouchableOpacity onPress={onClose} hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}>
            <SVGIcon name="close" size={totalSize(1.8)} />
          </TouchableOpacity>
        )}
      </View>

      <Tab
        value={tabIndex}
        onChange={setTabIndex}
        indicatorStyle={styles.indicatorStyle}
        style={styles.tabContainer}>
        <Tab.Item
          title="Share Link"
          titleStyle={[styles.tabTitle, tabIndex === 0 && styles.tabTitleActive]}
        />
        <Tab.Item
          title="Share via QR Code"
          titleStyle={[styles.tabTitle, tabIndex === 1 && styles.tabTitleActive]}
        />
      </Tab>

      <View style={styles.noteContainer}>
        <SVGIcon name="information" size={totalSize(1.6)} />
        <RNEText
          label="Links shared through apps like Messenger or Microsoft Teams may open in their own in-app browser instead of this app. Scanning the QR code is a more direct way to open this profile."
          variant="caption"
          color="grey"
          style={styles.noteText}
        />
      </View>

      {tabIndex === 0 ? (
        <View style={styles.panel}>
          <View style={styles.linkBox}>
            <RNEText
              label={shareUrl}
              variant="body"
              numberOfLines={1}
              ellipsizeMode="middle"
              style={styles.linkText}
            />
          </View>

          <View style={styles.actionsRow}>
            <RNEButton
              title="Copy Link"
              variant="outlined"
              color="secondary"
              icon={<SVGIcon name="copy" size={totalSize(1.6)} />}
              iconPosition="left"
              onPress={onCopyLink}
              style={styles.actionButton}
            />
            <RNEButton
              title="Share"
              color="secondary"
              icon={<SVGIcon name="share" size={totalSize(1.6)} />}
              iconPosition="left"
              onPress={onShareLink}
              style={styles.actionButton}
            />
          </View>
        </View>
      ) : (
        <View style={styles.panel}>
          <View style={styles.qrWrapper} collapsable={false}>
            <QRCode
              value={shareUrl}
              size={QR_SIZE}
              backgroundColor={colorPalette.white}
              getRef={getQrRef}
            />
          </View>

          <View style={styles.actionsRow}>
            <RNEButton
              title="Download"
              variant="outlined"
              color="secondary"
              icon={<SVGIcon name="download-down" size={totalSize(1.6)} />}
              iconPosition="left"
              onPress={onDownloadQr}
              style={styles.actionButton}
            />
            <RNEButton
              title="Share"
              color="secondary"
              icon={<SVGIcon name="share" size={totalSize(1.6)} />}
              iconPosition="left"
              onPress={onShareQr}
              style={styles.actionButton}
            />
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    width: Platform.OS === 'web' ? 420 : width(85),
    paddingHorizontal: Platform.OS === 'web' ? 24 : totalSize(2),
    paddingVertical: Platform.OS === 'web' ? 24 : totalSize(2.5),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: height(1.5),
  },
  tabContainer: {
    backgroundColor: 'transparent',
    marginBottom: height(1.5),
  },
  indicatorStyle: {
    backgroundColor: colorPalette.secondary,
    height: 3,
  },
  tabTitle: {
    fontSize: totalSize(1.1),
    fontFamily: 'Quicksand-SemiBold',
    color: colors.gray.default,
  },
  tabTitleActive: {
    color: colorPalette.secondary,
  },
  noteContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: width(1),
    backgroundColor: colors.gray.lighter,
    borderRadius: totalSize(0.8),
    padding: totalSize(1.2),
    marginBottom: height(2),
  },
  noteText: {
    flex: 1,
  },
  panel: {
    alignItems: 'center',
  },
  linkBox: {
    width: '100%',
    borderWidth: 1,
    borderColor: colors.gray.regular,
    borderRadius: totalSize(0.8),
    paddingHorizontal: totalSize(1.2),
    paddingVertical: totalSize(1.2),
    marginBottom: height(2),
  },
  linkText: {
    color: colorPalette.blackLight,
  },
  qrWrapper: {
    padding: totalSize(1.5),
    borderWidth: 1,
    borderColor: colors.gray.regular,
    borderRadius: totalSize(1),
    marginBottom: height(2),
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: width(2),
    width: '100%',
  },
  actionButton: {
    flex: 1,
    maxWidth: width(35),
  },
});

export default ShareDoctorProfilePanel;
